import { AgxMultiOptionButton } from '@urbanx/agx-ui-components';
import { ExecutiveRunRateMetricsResponse } from 'services/functions/rex/reporting/useExecutiveRunRateMetrics';
import { TableMetricsType } from './reporting';
import { ExecutiveKeyMetricsResponse } from 'services/functions/rex/reporting/useExecutiveKeyMetrics';

interface ExportDropdownProps {
  tableMetrics: TableMetricsType;
  keyMetricsData: ExecutiveKeyMetricsResponse | undefined;
  runRateData: ExecutiveRunRateMetricsResponse | undefined;
}

const downloadCsv = (csvString: string, filename: string) => {
  const blob = new Blob([csvString], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.setAttribute('hidden', '');
  a.setAttribute('href', url);
  a.setAttribute('download', filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const keyMetricsToCsv = (keyMetricsData: ExecutiveKeyMetricsResponse | undefined): string => {
  if (!keyMetricsData) return '';

  const headers = ['Metric', 'Settled', 'ProjectedSettled', 'Budget', 'Variance'];

  const csvRows = [
    headers.join(','),
    ...keyMetricsData.metrics.map((row) => {
      return [row.title, row.settled, row.projectedSettled, row.budget, row.variance].join(',');
    }),
  ].join('\n');

  return csvRows;
};

const runRateToCsv = (runRateData: ExecutiveRunRateMetricsResponse | undefined): string => {
  if (!runRateData) return '';

  const headers = ['Metric', 'MTD', 'Run Rate', 'Historical', 'Change'];

  const csvRows = [
    headers.join(','),
    ...runRateData.metrics.map((row) => {
      return [
        row.title,
        row.monthToDate,
        row.runRate,
        row.previousYearMonthToDate,
        row.percentageChange,
      ].join(',');
    }),
  ].join('\n');

  return csvRows;
};

const handleCSVExport = (
  tableMetrics: TableMetricsType,
  keyMetricsData: ExecutiveKeyMetricsResponse | undefined,
  runRateData: ExecutiveRunRateMetricsResponse | undefined
) => {
  let csvString = '';
  let filename = 'export.csv';
  const formatDateForFilename = (period: string): string => {
    return period.replace(' ', '');
  };

  if (tableMetrics === TableMetricsType.KeyMetrics) {
    const formattedDate = formatDateForFilename(keyMetricsData?.period || '');
    filename = `Key_metrics_${formattedDate}.csv`;
    csvString = keyMetricsToCsv(keyMetricsData);
  } else {
    filename = `Run_Rate_${runRateData?.period}.csv`;
    csvString = runRateToCsv(runRateData);
  }

  downloadCsv(csvString, filename);
};

const handlePDFExport = () => {
  window.print();
};

const ExportDropdown = ({ tableMetrics, keyMetricsData, runRateData }: ExportDropdownProps) => {
  const disableExportButton =
    (tableMetrics === TableMetricsType.KeyMetrics && !keyMetricsData) ||
    (tableMetrics === TableMetricsType.RunRate && !runRateData);

  return (
    <AgxMultiOptionButton
      primary
      medium
      options={[
        {
          text: 'PDF file',
          onClick: handlePDFExport,
        },
        {
          text: 'CSV file',
          onClick: () => handleCSVExport(tableMetrics, keyMetricsData, runRateData),
        },
      ]}
      text="Export"
      disabled={disableExportButton}
    />
  );
};

export default ExportDropdown;
